import type { Trip } from "@fairfleet/geotab";
import { odometerDistances } from "@/pages/logbook-audit/utils/odometerDistance";

export type TripWithOdometerStart<T extends Trip = Trip> = T & { odometerStartKm?: number };

/**
 * Adds the odometer start the logbook pages show: the end reading of the vehicle's previous trip,
 * not the end reading minus the GPS distance. The first trip of a vehicle in the loaded range has
 * no predecessor here and keeps no start (blank). Only valid for the unfiltered trips of a vehicle:
 * callers must not use it when a driver filter hides the vehicle's trips of other drivers.
 */
export function withOdometerStarts<T extends Trip>(trips: T[]): TripWithOdometerStart<T>[] {
  const byDevice = new Map<string, T[]>();
  for (const trip of trips) {
    const deviceId = trip.device?.id;
    if (!deviceId) continue;
    const list = byDevice.get(deviceId);
    if (list) list.push(trip);
    else byDevice.set(deviceId, [trip]);
  }

  const startByTrip = new Map<string, number>();
  for (const deviceTrips of byDevice.values()) {
    const distances = odometerDistances(
      deviceTrips.map(trip => ({
        id: trip.id,
        start: new Date(trip.start),
        stop: trip.stop ? new Date(trip.stop) : undefined,
        odometer: trip.odometer,
      })),
    );
    for (const [id, value] of distances) {
      if (value.odometerStartKm != null) startByTrip.set(id, value.odometerStartKm);
    }
  }

  return trips.map(trip => {
    const start = startByTrip.get(trip.id);
    return start == null ? trip : { ...trip, odometerStartKm: start };
  });
}
